import styles from './PickUpOrderView.module.scss';
import Avatar from '@components/Avatar';
import CopyTextToClipboard from '@components/CopyTextToClipboard';
import moment from 'moment/moment';
import PickUpOrderViewPoint from './components/PickUpOrderViewPoint';
import { AxiosResponse } from 'axios';
import { formatDate, formatSumFromObject } from '@utils/formatValues';
import { getPickUpXLSXFile } from '@api/apiClient';
import { IPickUpOrderViewProps, TSeller } from './types';
import { PickUpOrderForm } from '@api/apiClient/pickUp';
import { Text, TextColor, TextSize, TextType } from '@components/Text';
import { TInProcessOrder } from '@api/apiClient/fbp';
import { Tooltip } from '@components/new/Tooltip';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ExtLinkIcon from "@assets/icons/external-link.svg";

const pickUpStatusColors: Record<string, TextColor> = {
  'Canceled': TextColor.WARM_PINK,
  'Completed': TextColor.GREENISH,
  'Created': TextColor.DARK_MUTED,
  'InProcess': TextColor.DARK_MUTED
};

const fbpStatusColors: Record<string, TextColor> = {
  'Canceled': TextColor.WARM_PINK,
  'Completed': TextColor.GREENISH,
  'DamagedBySeller': TextColor.WARM_PINK,
  'DamagedByWarehouse': TextColor.WARM_PINK,
  'Lost': TextColor.WARM_PINK
};

const PickUpOrderView = <T extends PickUpOrderForm | TInProcessOrder>(props: IPickUpOrderViewProps<T>): React.ReactElement => {
  const { data, onClickOrderView } = props;

  const { pathname } = useLocation();
  const { t } = useTranslation();

  const condition = pathname === '/pick-up';

  const baseText = condition ? 'pickUp.tabOrders' : 'fbpPage.tabInProcess.table';
  const date = condition && 'createdAt' in data ? data.createdAt : '';

  const downloadingExcelFile = (): void => {
    getPickUpXLSXFile(data.id)
        .then((response: AxiosResponse) => {
          const file = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const fileURL = URL.createObjectURL(file);
          const element = document.createElement('a');
          element.setAttribute('href', fileURL);
          element.setAttribute('download', `${t('pickUp.tabOrders.xlsxFileName')} ${moment(date).format('DD/MM/YYYY HH:mm:ss')}`);
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
        });
  };

  return (
      <div
          className={styles.order}
          onClick={() => onClickOrderView(data)}
      >
        <PickUpOrderViewPoint
            label={t(`${baseText}.status`, { context: data.status }) || data.status}
            titleColor={condition ? pickUpStatusColors[data.status] : fbpStatusColors[data.status]}
            value={(
                <Text
                    size={TextSize.M}
                    type={TextType.BLOCK}
                >
                  {data.requestNumber}
                </Text>
            )}
            width="235px"
        />
        <PickUpOrderViewPoint
            label={t(`${baseText}.stores`)}
            value={data.sellers?.length ? (
                <div className={styles.sellers}>
                  {data.sellers.map((item: TSeller) => (
                      <Avatar
                          className={styles.seller}
                          id={item.id}
                          key={item.id}
                          name={item.name}
                          type="small"
                      />
                  ))}
                </div>
            ) : null}
        />
        <PickUpOrderViewPoint
            label={t(`${baseText}.items`)}
            value={(
                <div className={styles.wrapper}>
                  {condition && (
                      <Tooltip
                          action={
                            <button
                                className={styles.download}
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                  e.stopPropagation();
                                  downloadingExcelFile();
                                }}
                            />
                          }
                          text={t(`${baseText}.actionTooltip`)}
                          position="top"
                      />
                  )}
                  <Text size={TextSize.M}>
                    {(data.parcelsCount)}
                  </Text>
                </div>
            )}
            width="90px"
        />
        <PickUpOrderViewPoint
            label={t(`${baseText}.price`)}
            value={(
                <Text
                    ellipsis
                    size={TextSize.M}
                    width="85px"
                >
                  {formatSumFromObject(data.pickUpCost!)}
                </Text>
            )}
            width="100px"
            withBorder
        />
        <PickUpOrderViewPoint
            label={t(`${baseText}.address`)}
            value={(
                <Text
                    ellipsis
                    size={TextSize.M}
                    width="318px"
                >
                  {data.address?.rawLine}
                </Text>
            )}
            width="358px"
            withBorder
        />
        <PickUpOrderViewPoint
            label={t(`${baseText}.pickUpDate`)}
            value={(
                <Text size={TextSize.M}>
                  {formatDate(data.shipmentDate, 'dd.MM.yy')}
                </Text>
            )}
            width="155px"
            withBorder
        />
        {data.deliveryServiceName === "Kayue" ? (
          <PickUpOrderViewPoint
            label={(
              <a
                href="https://www.ky-express.com/88.html#tabPoint"
                className={styles.deliveryServiceNameLink}
                onClick={(e) => e.stopPropagation()}
                target={"_blank"}
              >
                {t("pickUp.courierService")}
                <img src={ExtLinkIcon} />
              </a>
            )}
            value={(
              <div onClick={(e) => e.stopPropagation()}>
                {data.deliveryTrackNumber ? (
                  <CopyTextToClipboard
                    text={data.deliveryTrackNumber!}
                  >
                    <Text size={TextSize.M}>
                      {data.deliveryTrackNumber}
                    </Text>
                  </CopyTextToClipboard>
                ) : null}
              </div>
            )}
            width="200px"
            withBorder
          />
        ) : (
          <PickUpOrderViewPoint
            label={t(`${baseText}.trackNumber`)}
            value={(
              <div onClick={(e) => e.stopPropagation()}>
                <CopyTextToClipboard
                  visibleOnHover
                  text={data.deliveryTrackNumber!}
                >
                  <Text size={TextSize.M}>
                    {data.deliveryTrackNumber}
                  </Text>
                </CopyTextToClipboard>
              </div>
            )}
            width="200px"
            withBorder
          />
        )}
      </div>
  );
};

export default PickUpOrderView;
